import React from "react";
import { RouteComponentProps } from "react-router";

import ToolbarMenuItem from "../ToolbarMenuItem";
import ViewToolbar from "../ViewToolbar";

import { V2 } from "../../Api";
import withTitle from "../withTitle";
import { formatDateLocal, formatMimeLong, formatSize, formatName } from "../../Utilities";
import FileUploadDialog from "../FileUploadDialog";
import DeletionConfirmationDialog from "../DeletionConfirmationDialog";
import HasRight from "../HasRight";

interface IHealthFairDocumentsViewState {
    isLoading: boolean | string;
    isUploadOpen: boolean;
    isDeleteOpen: boolean;

    documents: any[];

    deleteDescription: string;
    deleteId: string;
}

class HealthFairDocumentsView extends React.Component<RouteComponentProps<any>, IHealthFairDocumentsViewState> {
    constructor(props: Readonly<RouteComponentProps<any>>) {
        super(props);

        const state: IHealthFairDocumentsViewState = {
            isLoading: true,
            isUploadOpen: false,
            isDeleteOpen: false,

            documents: [],

            deleteDescription: "",
            deleteId: "",
        };

        this.state = state;

        this.openFile = this.openFile.bind(this);
        this.openUpload = this.openUpload.bind(this);
        this.closeUpload = this.closeUpload.bind(this);
        this.uploadSuccess = this.uploadSuccess.bind(this);
        this.openDelete = this.openDelete.bind(this);
        this.closeDelete = this.closeDelete.bind(this);
        this.deleteFile = this.deleteFile.bind(this);
    }

    public componentDidMount() {
        this.loadDocuments();
    }

    public render() {
        if (typeof this.state.isLoading === "string") {
            return <div className="viewer-root">
                <div className="error-box">{this.state.isLoading}</div>
            </div>;
        }

        if (this.state.isLoading === true) {
            return <div className="viewer-root spinner-full-root">
                <div className="spinner spinner-center lds-dual-ring-big" />
            </div>;
        }

        return (
            <div className="viewer-root">
                <ViewToolbar>
                    <ToolbarMenuItem right="fair:doc:upload">
                        <button type="button" className="icon-upload" title="Upload Document" onClick={this.openUpload} />
                    </ToolbarMenuItem>
                </ViewToolbar>
                <FileUploadDialog
                    isOpen={this.state.isUploadOpen} closeOnSuccess={true}
                    allowMultiple={true}
                    endpoint={`fairs/${this.props.match.params.id}/documents`}
                    onRequestClose={this.closeUpload} onUploadSuccess={this.uploadSuccess}
                    accept="application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,text/plain,image/*"
                />
                <DeletionConfirmationDialog isOpen={this.state.isDeleteOpen} description="Are you sure you want to delete this document?" onRequestClose={this.closeDelete} onConfirm={this.deleteFile}>
                    <p>{this.state.deleteDescription}</p>
                </DeletionConfirmationDialog>
                <table className="table-vertical">
                    <thead>
                        <tr>
                            <th className="col-smallest">Date Uploaded</th>
                            <th>Description</th>
                            <th className="col-smallest">Type</th>
                            <th className="col-smallest">Size</th>
                            <th className="col-smallest">Uploaded by</th>
                            <HasRight right="fair:doc:delete">
                                <th className="col-smallest" />
                            </HasRight>
                        </tr>
                    </thead>
                    <thead className="table-vertical-head-small">
                        <tr>
                            <th>Files</th>
                        </tr>
                    </thead>
                    <tbody>
                        {this.state.documents.length === 0
                            ? <tr>
                                <td colSpan={10}>No documents found</td>
                            </tr>
                            : this.state.documents.map(a => (
                                <tr key={a.fileId} className="clickable" onClick={() => this.openFile(a.fileId)}>
                                    <td>{formatDateLocal(a.creationTimestamp)}</td>
                                    <td className="wrap">{a.title}</td>
                                    <td>{formatMimeLong(a.mime)}</td>
                                    <td className="col-right-vt">{formatSize(a.contentLength)}</td>
                                    <td>{formatName(a.employee.name)}</td>
                                    <HasRight right="fair:doc:delete">
                                        <td className="col-nopad">
                                            <button type="button" className="button btn-light red small" title="Delete file" onClick={e => { e.stopPropagation(); this.openDelete(a) }}><i className="icon-trash" /></button>
                                        </td>
                                    </HasRight>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>
        );
    }

    private async loadDocuments() {
        try {
            this.setState({
                isLoading: true
            });

            const documents = await V2.get<any[]>(`fairs/${this.props.match.params.id}/documents`);

            this.setState({
                isLoading: false,

                documents
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || e || "Could not load documents"
            });
        }
    }

    private openFile(id: string) {
        this.props.history.push(`/fairs/${this.props.match.params.id}/documents/${id}`);
    }

    private openUpload() {
        this.setState({
            isUploadOpen: true
        });
    }

    private closeUpload() {
        this.setState({
            isUploadOpen: false
        });
    }

    private uploadSuccess() {
        window.setTimeout(() => this.loadDocuments(), 100);
    }

    private openDelete(file: any) {
        this.setState({
            isDeleteOpen: true,

            deleteId: file.fileId,
            deleteDescription: file.title
        });
    }

    private closeDelete() {
        this.setState({
            isDeleteOpen: false
        });
    }

    private async deleteFile() {
        await V2.delete(`fairs/${this.props.match.params.id}/documents/${this.state.deleteId}`);

        window.setTimeout(() => this.loadDocuments(), 100);
    }
}

export default withTitle("Health Fair Documents", HealthFairDocumentsView);
